// src/pages/BlogEditor.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import '../Blog.css';

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isWriter } = useAuth();
  const editorRef = useRef(null);

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState(null);

  // SEO fields
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoTitleEdited, setSeoTitleEdited] = useState(false);
  const [seoDescriptionEdited, setSeoDescriptionEdited] = useState(false);
  const [showSeoSection, setShowSeoSection] = useState(false);

  const categories = [
    'Benefits Strategy',
    'Cost Management',
    'Industry Trends',
    'Technology',
    'Case Studies',
    'Compliance',
    'Wellness',
    'Data Analytics'
  ];

  // Auto-generate SEO title from post title
  useEffect(() => {
    if (!seoTitleEdited && title) {
      const generated = `${title} | Avenir Insights`;
      setSeoTitle(generated.length > 60 ? `${title.substring(0, 50)}... | Avenir` : generated);
    }
  }, [title, seoTitleEdited]);

  // Auto-generate SEO description from subtitle or content
  useEffect(() => {
    if (!seoDescriptionEdited) {
      let generated = '';
      if (subtitle) {
        generated = subtitle;
      } else if (content) {
        // Strip any markdown/formatting and get first ~155 chars
        const plainText = content
          .replace(/\*\*(.+?)\*\*/g, '$1')
          .replace(/_(.+?)_/g, '$1')
          .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
          .replace(/^##?\s*/gm, '')
          .replace(/^>\s*/gm, '')
          .replace(/^[•-]\s*/gm, '')
          .replace(/\n+/g, ' ')
          .trim();
        generated = plainText.substring(0, 155);
        if (plainText.length > 155) generated += '...';
      }
      setSeoDescription(generated);
    }
  }, [subtitle, content, seoDescriptionEdited]);

  useEffect(() => {
    if (!isWriter) {
      navigate('/blog');
      return;
    }

    if (id) {
      fetchPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isWriter, navigate]);

  const fetchPost = async () => {
    try {
      const docRef = doc(db, 'posts', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const ADMIN_EMAILS = ['ari@avenir.com.ai'];
        if (data.authorId !== currentUser.uid && !ADMIN_EMAILS.includes(currentUser.email)) {
          setError('You can only edit your own articles');
          setLoading(false);
          return;
        }
        setTitle(data.title || '');
        setSubtitle(data.subtitle || '');
        setContent(htmlToText(data.content || ''));
        setCategory(data.category || '');
        setTags(data.tags?.join(', ') || '');
        setCoverImage(data.coverImage || '');

        // Load SEO fields if they exist
        if (data.seoTitle) {
          setSeoTitle(data.seoTitle);
          setSeoTitleEdited(true);
        }
        if (data.seoDescription) {
          setSeoDescription(data.seoDescription);
          setSeoDescriptionEdited(true);
        }
      } else {
        setError('Article not found');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Failed to load article');
      setLoading(false);
    }
  };

  // Convert HTML back to editable text
  const htmlToText = (html) => {
    return html
      .replace(/<a[^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>/gi, '[$2]($1)')
      .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<b>(.*?)<\/b>/gi, '**$1**')
      .replace(/<em>(.*?)<\/em>/gi, '_$1_')
      .replace(/<i>(.*?)<\/i>/gi, '_$1_')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '\n## $1\n')
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '\n## $1\n')
      .replace(/<blockquote[^>]*>(.*?)<\/blockquote>/gi, '\n> $1\n')
      .replace(/<li>(.*?)<\/li>/gi, '• $1\n')
      .replace(/<\/?ul>/gi, '\n')
      .replace(/<\/?ol>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Convert markdown-style text to HTML
  const textToHtml = (text) => {
    const lines = text.split('\n');
    let html = '';
    let inList = false;
    let currentParagraph = '';

    const processInlineFormatting = (text) => {
      return text
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/(?<![a-zA-Z0-9])_([^_]+)_(?![a-zA-Z0-9])/g, '<em>$1</em>');
    };

    const flushParagraph = () => {
      if (currentParagraph.trim()) {
        html += `<p>${processInlineFormatting(currentParagraph.trim())}</p>`;
        currentParagraph = '';
      }
    };

    const flushList = () => {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        flushParagraph();
        flushList();
        continue;
      }

      if (trimmedLine.startsWith('##')) {
        flushParagraph();
        flushList();
        const headingText = trimmedLine.replace(/^##\s*/, '');
        html += `<h2>${processInlineFormatting(headingText)}</h2>`;
        continue;
      }

      if (trimmedLine.startsWith('#') && !trimmedLine.startsWith('##')) {
        flushParagraph();
        flushList();
        const headingText = trimmedLine.replace(/^#\s*/, '');
        html += `<h2>${processInlineFormatting(headingText)}</h2>`;
        continue;
      }

      if (trimmedLine.startsWith('> ')) {
        flushParagraph();
        flushList();
        const quoteText = trimmedLine.substring(2);
        html += `<blockquote>${processInlineFormatting(quoteText)}</blockquote>`;
        continue;
      }

      if (trimmedLine.startsWith('• ') || trimmedLine.startsWith('- ')) {
        flushParagraph();
        if (!inList) {
          html += '<ul>';
          inList = true;
        }
        const bulletText = trimmedLine.substring(2);
        html += `<li>${processInlineFormatting(bulletText)}</li>`;
        continue;
      }

      if (currentParagraph) {
        currentParagraph += ' ' + trimmedLine;
      } else {
        currentParagraph = trimmedLine;
      }
    }

    flushParagraph();
    flushList();

    html = html
      .replace(/<p>##\s*(.+?)<\/p>/g, '<h2>$1</h2>')
      .replace(/<p>#\s*(.+?)<\/p>/g, '<h2>$1</h2>');

    return html;
  };

  const insertFormatting = (before, after = '') => {
    const textarea = editorRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const newText = content.substring(0, start) + before + selectedText + after + content.substring(end);

    setContent(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  const handleSeoTitleChange = (value) => {
    setSeoTitle(value);
    setSeoTitleEdited(true);
  };

  const handleSeoDescriptionChange = (value) => {
    setSeoDescription(value);
    setSeoDescriptionEdited(true);
  };

  const resetSeoTitle = () => {
    setSeoTitleEdited(false);
    const generated = `${title} | Avenir Insights`;
    setSeoTitle(generated.length > 60 ? `${title.substring(0, 50)}... | Avenir` : generated);
  };

  const resetSeoDescription = () => {
    setSeoDescriptionEdited(false);
    // Will trigger the useEffect to regenerate
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!content.trim()) {
      setError('Content is required');
      return;
    }

    setSaving(true);
    setError(null);

    const postData = {
      title: title.trim(),
      subtitle: subtitle.trim(),
      content: textToHtml(content),
      category: category,
      tags: tags.split(',').map(t => t.trim()).filter(t => t),
      coverImage: coverImage.trim(),
      authorId: currentUser.uid,
      authorName: currentUser.displayName || 'Avenir Team',
      authorEmail: currentUser.email,
      authorPhotoURL: currentUser.photoURL || null,
      updatedAt: new Date().toISOString(),
      // SEO fields
      seoTitle: seoTitle.trim() || `${title.trim()} | Avenir Insights`,
      seoDescription: seoDescription.trim() || subtitle.trim() || title.trim()
    };

    try {
      if (id) {
        await updateDoc(doc(db, 'posts', id), postData);
        navigate(`/blog/${id}`);
      } else {
        postData.createdAt = new Date().toISOString();
        const docRef = await addDoc(collection(db, 'posts'), postData);
        navigate(`/blog/${docRef.id}`);
      }
    } catch (err) {
      console.error('Error saving post:', err);
      setError('Failed to save article. Please try again.');
      setSaving(false);
    }
  };

  if (!isWriter) {
    return null;
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="blog-container">
          <div className="blog-loading">
            <div className="loading-spinner"></div>
            <p>Loading editor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="editor-container">
        {/* Editor Header */}
        <header className="editor-header">
          <Link to="/blog" className="back-link">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </Link>
          <h1 className="editor-title">
            {id ? 'Edit Article' : 'Write a New Article'}
          </h1>
        </header>

        {/* Error Message */}
        {error && (
          <div className="editor-error">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Editor Form */}
        <form onSubmit={handleSubmit} className="editor-form">
          {/* Title */}
          <div className="form-group">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Article Title"
              className="title-input"
              maxLength={200}
            />
          </div>

          {/* Subtitle */}
          <div className="form-group">
            <input
              type="text"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Subtitle (optional)"
              className="subtitle-input"
              maxLength={300}
            />
          </div>

          {/* Meta Fields */}
          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="form-select"
              >
                <option value="">Select a category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="e.g., healthcare, self-insurance, cost savings"
                className="form-input"
              />
            </div>
          </div>

          {/* Cover Image */}
          <div className="form-group">
            <label>Cover Image URL (optional)</label>
            <input
              type="url"
              value={coverImage}
              onChange={(e) => setCoverImage(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="form-input"
            />
            {coverImage && (
              <div className="image-preview">
                <img src={coverImage} alt="Cover preview" onError={(e) => e.target.style.display = 'none'} />
              </div>
            )}
          </div>

          {/* SEO Section */}
          <div className="form-group seo-section">
            <button
              type="button"
              className="seo-toggle"
              onClick={() => setShowSeoSection(!showSeoSection)}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ transform: showSeoSection ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span>SEO Settings</span>
              <span className="seo-status">
                {seoTitle && seoDescription ? '✓ Configured' : 'Auto-generated'}
              </span>
            </button>

            {showSeoSection && (
              <div className="seo-fields">
                <div className="seo-field">
                  <div className="seo-field-header">
                    <label>SEO Title</label>
                    <span className={`char-count ${seoTitle.length > 60 ? 'over' : ''}`}>
                      {seoTitle.length}/60
                    </span>
                    {seoTitleEdited && (
                      <button type="button" className="reset-btn" onClick={resetSeoTitle}>
                        Reset to auto
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={seoTitle}
                    onChange={(e) => handleSeoTitleChange(e.target.value)}
                    placeholder="SEO-optimized title for search engines"
                    className="form-input"
                    maxLength={70}
                  />
                  <p className="seo-hint">Recommended: 50-60 characters. This appears in search results.</p>
                </div>

                <div className="seo-field">
                  <div className="seo-field-header">
                    <label>Meta Description</label>
                    <span className={`char-count ${seoDescription.length > 160 ? 'over' : ''}`}>
                      {seoDescription.length}/160
                    </span>
                    {seoDescriptionEdited && (
                      <button type="button" className="reset-btn" onClick={resetSeoDescription}>
                        Reset to auto
                      </button>
                    )}
                  </div>
                  <textarea
                    value={seoDescription}
                    onChange={(e) => handleSeoDescriptionChange(e.target.value)}
                    placeholder="Brief description for search engine results"
                    className="form-input seo-textarea"
                    maxLength={200}
                    rows={3}
                  />
                  <p className="seo-hint">Recommended: 150-160 characters. This appears below the title in search results.</p>
                </div>

                {/* SEO Preview */}
                <div className="seo-preview">
                  <p className="seo-preview-label">Search Result Preview</p>
                  <div className="seo-preview-box">
                    <div className="seo-preview-title">{seoTitle || 'Article Title | Avenir Insights'}</div>
                    <div className="seo-preview-url">avenir.com.ai/blog/article-slug</div>
                    <div className="seo-preview-description">
                      {seoDescription || 'Article description will appear here...'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Content Editor */}
          <div className="form-group editor-wrapper">
            <label>Content</label>

            {/* Simple Toolbar */}
            <div className="simple-toolbar">
              <button
                type="button"
                onClick={() => insertFormatting('**', '**')}
                className="toolbar-btn"
                title="Bold (**text**)"
              >
                <strong>B</strong>
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('_', '_')}
                className="toolbar-btn"
                title="Italic (_text_)"
              >
                <em>I</em>
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('\n## ', '\n')}
                className="toolbar-btn"
                title="Heading (## text)"
              >
                H
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('\n> ', '\n')}
                className="toolbar-btn"
                title="Quote (> text)"
              >
                "
              </button>
              <button
                type="button"
                onClick={() => {
                  const url = prompt('Enter URL:');
                  if (url) {
                    insertFormatting('[', `](${url})`);
                  }
                }}
                className="toolbar-btn"
                title="Link ([text](url))"
              >
                🔗
              </button>
              <button
                type="button"
                onClick={() => insertFormatting('\n• ', '')}
                className="toolbar-btn"
                title="Bullet (• text)"
              >
                •
              </button>
              <span className="toolbar-hint">
                **bold** · _italic_ · [link](url) · ## heading
              </span>
            </div>

            <textarea
              ref={editorRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={`Write your article here...

**bold text** makes words stand out
_italic text_ for emphasis
[link text](https://example.com) creates a clickable link
## Heading creates a section title
> This becomes a blockquote
• This is a bullet point

Leave a blank line between paragraphs.`}
              className="content-textarea"
              rows={20}
            />
          </div>

          {/* Submit Buttons */}
          <div className="editor-actions">
            <Link to="/blog" className="btn btn-secondary">
              Cancel
            </Link>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="btn-spinner"></span>
                  {id ? 'Updating...' : 'Publishing...'}
                </>
              ) : (
                id ? 'Update Article' : 'Publish Article'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogEditor;